import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { EventCalendar } from "@/components/EventCalendar";
import { EventFormModal } from "@/components/EventFormModal";
import { Plus, Edit, Trash2, Search } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [visFilter, setVisFilter] = useState("all");

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setOpen(true); };

  const remove = async (id) => {
    if (!window.confirm("Delete event?")) return;
    await api.delete(`/admin/events/${id}`);
    load();
  };

  const filtered = useMemo(() => events.filter((e) => {
    if (search && !`${e.title} ${e.short_title}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (catFilter !== "all" && e.category !== catFilter) return false;
    if (statusFilter !== "all" && e.event_status !== statusFilter) return false;
    if (visFilter !== "all" && e.visibility !== visFilter) return false;
    return true;
  }), [events, search, catFilter, statusFilter, visFilter]);

  return (
    <AdminLayout title="Events">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Event Calendar Management</h1>
          <p className="mt-1 text-muted-foreground">Manage events, webinars, and programs.</p>
        </div>
        <Button onClick={openAdd} className="btn-glow" data-testid="add-event-btn"><Plus className="h-4 w-4" /> Add Event</Button>
      </div>

      <Tabs defaultValue="calendar">
        <TabsList>
          <TabsTrigger value="calendar" data-testid="tab-cal">Calendar</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">List</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-6">
          <Card className="glass-card">
            <CardContent className="p-4 sm:p-6">
              <EventCalendar events={filtered} onEventClick={openEdit} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list" className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search event..." className="pl-9" />
            </div>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Kategori</SelectItem>
                <SelectItem value="percuma">Free</SelectItem>
                <SelectItem value="berbayar">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Status</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="open_registration">Open Reg</SelectItem>
                <SelectItem value="full">Full</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={visFilter} onValueChange={setVisFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Visibility</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card className="glass-card">
            <CardContent className="p-0">
              <div className="table-wrap">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Visibility</TableHead>
                      <TableHead>Link</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No events.</TableCell></TableRow>
                    ) : filtered.map((ev) => (
                      <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
                        <TableCell className="font-medium">{ev.title}</TableCell>
                        <TableCell>{ev.event_date}</TableCell>
                        <TableCell>{ev.start_time}–{ev.end_time}</TableCell>
                        <TableCell><StatusBadge status={ev.category} /></TableCell>
                        <TableCell><StatusBadge status={ev.event_status} /></TableCell>
                        <TableCell><StatusBadge status={ev.visibility} /></TableCell>
                        <TableCell className="text-xs">{ev.registration_link ? <span className="text-teal">Has link</span> : <span className="text-muted-foreground">No link</span>}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.id}`}><Edit className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(ev.id)} data-testid={`delete-event-${ev.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EventFormModal event={editing} open={open} onOpenChange={setOpen} onSaved={load} />
    </AdminLayout>
  );
}
